import { format, startOfWeek, endOfWeek, addDays, isSameDay, addHours, startOfDay } from "date-fns"
import { cn } from "@/lib/utils"
import { Avatar, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet"
import type { CalendarEvent } from "./calendar"
import UserSvg from "@/assets/userSvg.svg"
import { CalendarEventActions } from "./calendar-event-actions"
import { useState } from "react"

interface WeekViewProps {
  currentDate: Date
  events: CalendarEvent[]
  dateRange: { start: Date; end: Date }
  onViewEvent?: (event: CalendarEvent) => void
  onEditEvent?: (event: CalendarEvent) => void
  onDeleteEvent?: (event: CalendarEvent) => void
  refetch?: () => void
  access?: string
}

// Map color to static Tailwind classes
const getColorClasses = (color?: string) => {
  const colorMap: Record<string, string> = {
    purple: "bg-purple-50 border-purple-200 hover:bg-purple-100",
    blue: "bg-blue-50 border-blue-200 hover:bg-blue-100",
    green: "bg-green-50 border-green-200 hover:bg-green-100",
    red: "bg-red-50 border-red-200 hover:bg-red-100",
    yellow: "bg-yellow-50 border-yellow-200 hover:bg-yellow-100",
    orange: "bg-orange-50 border-orange-200 hover:bg-orange-100",
    teal: "bg-teal-50 border-teal-200 hover:bg-teal-100",
    pink: "bg-pink-50 border-pink-200 hover:bg-pink-100",
    indigo: "bg-indigo-50 border-indigo-200 hover:bg-indigo-100",
    gray: "bg-gray-50 border-gray-200 hover:bg-gray-100",
  };
  return colorMap[color || ""] || "bg-blue-50 border-blue-200 hover:bg-blue-100";
};

// Component to render a single event
const EventCard = ({
  event,
  onViewEvent,
  onEditEvent,
  onDeleteEvent,
  refetch,
  access,
  style,
  compact = false
}: {
  event: CalendarEvent;
  onViewEvent?: (event: CalendarEvent) => void;
  onEditEvent?: (event: CalendarEvent) => void;
  onDeleteEvent?: (event: CalendarEvent) => void;
  refetch?: () => void;
  access?: string;
  style?: React.CSSProperties;
  compact?: boolean;
}) => (
  <div
    className={cn(
      "rounded-md border overflow-hidden transition-colors",
      compact ? "p-1" : "p-2",
      getColorClasses(event.color)
    )}
    style={style}
  >
    <div className="flex items-start justify-between gap-1">
      <div
        className={cn(
          "font-medium truncate flex-1 cursor-pointer",
          compact ? "text-[10px]" : "text-xs"
        )}
        onClick={() => onViewEvent?.(event)}
      >
        {event.title}
      </div>
      <CalendarEventActions
        event={event}
        onView={onViewEvent}
        onEdit={onEditEvent}
        onDelete={onDeleteEvent}
        refetch={refetch}
        access={access}
        size="sm"
        variant="dropdown"
      />
    </div>
    {event.tags && !compact && (
      <div className="flex flex-wrap gap-1 mt-1">
        {event.tags.slice(0, 2).map((tag, idx) => (
          <span
            key={idx}
            className={cn(
              "px-1.5 py-0.5 rounded-sm text-white text-[10px]",
              `bg-${tag.color}-500`
            )}
          >
            {tag.label}
          </span>
        ))}
        {event.tags.length > 2 && (
          <span className="text-[10px] text-gray-500">+{event.tags.length - 2}</span>
        )}
      </div>
    )}
    {event.tags && compact && (
      <div className="flex gap-1 mt-0.5">
        {event.tags.slice(0, 1).map((tag, idx) => (
          <span
            key={idx}
            className={cn(
              "px-1 py-0.5 rounded-sm text-white text-[8px]",
              `bg-${tag.color}-500`
            )}
          >
            {tag.label}
          </span>
        ))}
      </div>
    )}
    {event.user && !compact && (
      <div className="relative bottom-0 flex justify-end">
        <Avatar className="absolute h-4 w-4">
          <AvatarImage src={UserSvg} />
        </Avatar>
      </div>
    )}
  </div>
);

export function WeekView({ currentDate, events, dateRange, onViewEvent, onEditEvent, onDeleteEvent, refetch, access }: WeekViewProps) {
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<{ day: Date; hour: number; events: CalendarEvent[] } | null>(null);
  const startDate = startOfWeek(currentDate, { weekStartsOn: 1 }); // Start from Monday to match slot generation
  const endDate = endOfWeek(startDate, { weekStartsOn: 1 });

  const hours = Array.from({ length: 24 }, (_, i) => i); // 0:00 to 23:00
  const days = Array.from({ length: 7 }, (_, i) => addDays(startDate, i));


  // Get events for a specific day and hour
  const getEventsForTimeSlot = (day: Date, hour: number) => {
    const hourStart = addHours(startOfDay(day), hour);
    const hourEnd = addHours(startOfDay(day), hour + 1);

    return events.filter((event) => {
      const eventStart = new Date(event.start);
      
      // Only show the event in the hour slot where it starts
      // This prevents duplicates across multiple hour slots
      return isSameDay(eventStart, day) &&
        eventStart >= hourStart &&
        eventStart < hourEnd;
    });
  };

  return (
    <div className="absolute inset-0 flex flex-col overflow-hidden">
      <div className="sticky top-0 z-10 grid grid-cols-8 border-b bg-white shrink-0">
        <div className="border-r py-2 text-center text-xs"></div>
        {days.map((day, index) => (
          <div
            key={day.toString()}
            className={cn(
              "py-2 text-center text-sm border-r",
              // index === 3 && "bg-green-50/30" // Highlight Wednesday like in the screenshot
            )}
          >
            <p className="text-xs">{format(day, "EEEE")}</p>
            <p className="text-[11px]">{format(day, "MMM d")}</p>
          </div>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto">
        <div className="grid grid-cols-8">
          <div className="sticky left-0 z-10 border-r bg-white">
            {hours.map((hour) => (
              <div
                key={hour}
                className="h-24 border-b px-2 text-xs text-gray-500 relative"
              >
                <div className="absolute -top-2.5">{hour}:00</div>
              </div>
            ))}
          </div>
          {days.map((day, index) => (
            <div
              key={day.toString()}
              className={cn(
                "border-r",
                // index === 3 && "bg-green-50/30" // Highlight Wednesday like in the screenshot
              )}
            >
              {hours.map((hour) => {
                const eventsForSlot = getEventsForTimeSlot(day, hour);
                const maxVisibleEvents = 2; // Show max 2 events directly in the slot

                return (
                  <div key={hour} className="h-24 border-b p-1 relative">
                    {eventsForSlot.length === 0 ? null : eventsForSlot.length === 1 ? (
                      // Single event - show full event
                      (() => {
                        const event = eventsForSlot[0];
                        const eventStart = new Date(event.start);
                        const eventEnd = new Date(event.end);
                        const startMinutes = eventStart.getMinutes();
                        const top = (startMinutes / 60) * 100;
                        const height = Math.min(
                          ((eventEnd.getTime() - eventStart.getTime()) / (1000 * 60 * 60)) * 100,
                          100 - top
                        );

                        return (
                          <div
                            key={event.id}
                            className="absolute left-1 right-1"
                            style={{
                              top: `${top}%`,
                              height: `${height}%`,
                              zIndex: 10
                            }}
                          >
                            <EventCard
                              event={event}
                              onViewEvent={onViewEvent}
                              onEditEvent={onEditEvent}
                              onDeleteEvent={onDeleteEvent}
                              refetch={refetch}
                              access={access}
                            />
                          </div>
                        );
                      })()
                    ) : (
                      // Multiple events - show only "View All Events" button for cleaner UI
                      <div className="absolute inset-1 flex flex-col justify-center">
                        <Sheet>
                          <SheetTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="w-full h-full min-h-[60px] bg-gradient-to-br from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 border-2 border-blue-200 hover:border-blue-300 text-blue-800 font-medium shadow-sm hover:shadow-md transition-all duration-200 flex flex-col items-center justify-center gap-1"
                              onClick={() => setSelectedTimeSlot({ day, hour, events: eventsForSlot })}
                            >
                              <div className="text-lg font-bold">{eventsForSlot.length}</div>
                              <div className="text-xs">
                                {eventsForSlot.length === 1 ? 'Slot' : 'Slots'}
                              </div>
                              <div className="text-[10px] opacity-70 text-nowrap">View more</div>
                            </Button>
                          </SheetTrigger>
                          <SheetContent fixCloseButton reverseCloseButton className="w-[400px] sm:w-[540px] bg-formbg p-0 custom-scrollbar h-full">
                            <SheetHeader className="p-4 sticky top-0 z-40 py-2  shadow-sm bg-white">
                              <div className="flex items-center justify-between gap-2 w-full">

                                <SheetTitle className="pl-6">
                                  Slots for {format(day, "EEEE, MMM d yyyy")}
                                </SheetTitle>
                              </div>

                            </SheetHeader>
                            <div className="h-full p-4">
                              <div className=" p-4 bg-white rounded-lg space-y-4  ">
                                <>
                                  <div className="text-sm font-medium text-gray-700">
                                    {eventsForSlot.length} Slots scheduled
                                  </div>
                                </>
                                {eventsForSlot.map((event, index) => (
                                  <EventCard
                                    event={event}
                                    onViewEvent={onViewEvent}
                                    onEditEvent={onEditEvent}
                                    onDeleteEvent={onDeleteEvent}
                                    refetch={refetch}
                                    access={access}
                                  />
                                ))}
                              </div>
                            </div>
                          </SheetContent>
                        </Sheet>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

